import { Box, Paper, Stack, Typography } from '@mui/material'
import { HeadCell, Order } from '@common/components/Table/table.types'
import { MouseEvent, useState } from 'react'
import EnhancedTable from '@common/components/Table/EnhancedTable'
import { TimepointSubject } from '@common/config/api/client'
import SubjectsTimepointsTableBody from './SubjectsTimepointsTableBody'
import useStudySubjectsTimepointsTableHandlers from '@features/subject/hooks/useStudySubjectsTimepointsTableHandlers'
import SubjectsTimepointsTableToolbar from '@features/subject/components/SubjectsTimepointsTable/SubjectsTimepointsTableToolbar'
import { useUiStore } from '@features/qc_task_details_view/store/uiStore'
import { useFormContext } from 'react-hook-form'
import { ExamReassignementFormValues } from '../../schemas/ExamReassignementEsignValidationSchema'
import { useGetSubjectsList } from '@features/subject/hooks/useGetSubjectsList'
import CreateStudySubjectDialog from '@features/study_details/Subjects/components/CreateStudySubjectDialog'
import { queryClient } from '@common/config/api/queryClient'

export default function SubjectsTimepointsTable({
  assignableTimepointSubmissions,
}: {
  assignableTimepointSubmissions: string[]
}) {
  const {
    subjectsTimepointsParams,
    page,
    handleSearch,
    handleAddClick,
    handlePageChange,
    handleRowsPerPageChange,
    isSubjectDialogVisible,
    setIsSubjectDialogVisible,
  } = useStudySubjectsTimepointsTableHandlers()

  const { data, error, isLoading } = useGetSubjectsList(['SubjectsList', 'ReassignExam'], subjectsTimepointsParams)
  const timepointSubjects = data?.data

  const [order, setOrder] = useState<Order>('asc')
  const [orderBy, setOrderBy] = useState<keyof TimepointSubject>('pseudo_id')

  const { selectedTempSubjectPseudoId, selectedTempTimepointSubmissionPseudoId } = useUiStore()
  const { setValue } = useFormContext<ExamReassignementFormValues>()

  const headCells: readonly HeadCell<TimepointSubject>[] = [
    { disablePadding: false, id: 'pseudo_id', label: 'Subject', disableSort: false, stickyColumn: true, width: 170 },
    {
      disablePadding: false,
      id: 'site_id' as keyof TimepointSubject,
      label: 'Site ID',
      disableSort: false,
      stickyColumn: true,
      width: 170,
    },
    { disablePadding: false, id: 'status', label: 'Status', disableSort: false, stickyColumn: true, width: 170 },
    {
      disablePadding: false,
      id: 'query_status' as keyof TimepointSubject,
      label: 'Query',
      disableSort: false,
      stickyColumn: true,
      width: 170,
    },
    ...(timepointSubjects?.study_timepoints || []).map((tp) => ({
      disablePadding: false,
      id: tp.timepoint_id as keyof TimepointSubject,
      label: tp.name,
      disableSort: true,
      minWidth: 220,
    })),
  ]

  const handleRequestSort = (_event: MouseEvent<unknown>, property: keyof TimepointSubject) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleSelectTimepointSubmission = (timepointSubmissionId: string, subjectId: string) => {
    setValue('to_timepoint_submission.timepoint_submission_id', timepointSubmissionId, { shouldValidate: true })
    setValue('selected_subject_id', subjectId, { shouldValidate: true })
  }

  return (
    <Box component="section">
      <Stack paddingTop={5}>
        <Typography variant="h4" color="text.primary">
          Choose a new timepoint
        </Typography>
        <Typography variant="body1" color="text.secondary">
          You can choose a timepoint from the same subject or a different one
        </Typography>
      </Stack>

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" spacing={4}>
          <Stack direction="row" alignItems="center" spacing={0.25}>
            <Typography variant="body2" color="text.secondary">
              Selected subject:
            </Typography>
            <Typography variant="subtitle2" color="text.primary">
              {selectedTempSubjectPseudoId || '-'}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.25}>
            <Typography variant="body2" color="text.secondary">
              Selected timepoint:
            </Typography>
            <Typography variant="subtitle2" color="text.primary">
              {selectedTempTimepointSubmissionPseudoId || '-'}
            </Typography>
          </Stack>
        </Stack>
        <SubjectsTimepointsTableToolbar
          onSearch={handleSearch}
          searchValue={subjectsTimepointsParams.search}
          onAddClick={handleAddClick}
        />
      </Stack>
      <Paper
        sx={{
          padding: 3,
          borderRadius: 4,
        }}
      >
        {isLoading && !timepointSubjects ? (
          <div>Loading...</div>
        ) : error ? (
          <div>Error: {error.message}</div>
        ) : (
          <EnhancedTable<TimepointSubject>
            rows={[]}
            page={page}
            headCells={headCells}
            orderBy={orderBy}
            order={order}
            expandable
            rowsPerPage={subjectsTimepointsParams.limit}
            onRequestSort={handleRequestSort}
            onChangePage={(event, newPage) => handlePageChange(timepointSubjects, event, newPage)}
            onChangeRowsPerPage={handleRowsPerPageChange}
            next_cursor={timepointSubjects?.pagination?.next_cursor}
            previous_cursor={timepointSubjects?.pagination?.previous_cursor}
          >
            <SubjectsTimepointsTableBody
              rows={timepointSubjects?.subjects || []}
              assignableTimepointSubmissions={assignableTimepointSubmissions}
              order={order}
              orderBy={orderBy}
              handleSelectTimepointSubmission={handleSelectTimepointSubmission}
            />
          </EnhancedTable>
        )}
      </Paper>
      {isSubjectDialogVisible ? (
        <CreateStudySubjectDialog
          onClose={(created) => {
            setIsSubjectDialogVisible(false)
            if (created) {
              queryClient.invalidateQueries({ queryKey: ['SubjectsList'] })
            }
          }}
        />
      ) : null}
    </Box>
  )
}
